// ApplicantsPipelineScreen.js — Full Recruitment Pipeline
// Kanban + List views · Real API · Navigate to ApplicantDetailsScreen · Stage drag-move

import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView,
  RefreshControl, StatusBar, Alert, Modal, FlatList, ActivityIndicator,
  TextInput, Image, Platform, Dimensions,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  RecruitmentAPI, STAGES, STATUS_CFG, normaliseApplicant,
  avatarColor, initials, timeAgo, fmtDate, loadCompanyData, getStatusColor,
} from './recruitmentShared';
import { apiFetch } from '../../../utils/apiClient';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96';

// ── Avatar ─────────────────────────────────────────────────────────────────────
const Avatar = ({ name, uri, size = 36 }) => {
  const [err, setErr] = React.useState(false);
  if (uri && !err) return (
    <View style={{ width: size, height: size, borderRadius: size / 2, overflow: 'hidden' }}>
      <Image source={{ uri }} style={{ width: size, height: size }} onError={() => setErr(true)} />
    </View>
  );
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: avatarColor(name), alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ color: '#fff', fontSize: size * 0.32, fontWeight: '900' }}>{initials(name)}</Text>
    </View>
  );
};

export default function ApplicantsPipelineScreen({ navigation, route }) {
  if (!route) route = { params: {} };
  const { jobId, jobTitle, filterByJob = true } = route?.params || {};

  const [companyLogo, setCompanyLogo]   = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName]   = useState(route?.params?.companyName || '');
  const [companyId, setCompanyId]       = useState(null);

  const [applicants, setApplicants]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [viewMode, setViewMode]         = useState('kanban'); // 'kanban' | 'list'
  const [activeTab, setActiveTab]       = useState('pipeline');
  const [search, setSearch]             = useState('');
  const [filterStage, setFilterStage]   = useState('all');
  const [saving, setSaving]             = useState(false);

  // Modal
  const [detailApplicant, setDetailApplicant] = useState(null);
  const [addModal, setAddModal]         = useState(false);
  const [formData, setFormData]         = useState({});

  useFocusEffect(useCallback(() => {
    loadCompanyData(setCompanyLogo, setCompanyName, setCompanyId);
  }, []));

  useEffect(() => { init(); }, []);

  const init = async () => {
    setLoading(true);
    // Resolve company_id from route → AsyncStorage. Never default to 1, which
    // would silently leak data from company 1 if the storage key is empty.
    const cid = route?.params?.company_id
      || parseInt(await AsyncStorage.getItem('company_id') || '0', 10)
      || null;
    if (!cid) {
      setLoading(false);
      Alert.alert('No company', 'Could not determine your company. Please sign in again.');
      return;
    }
    setCompanyId(cid);
    await fetchApplicants(cid);
    setLoading(false);
  };

  const fetchApplicants = async (cid) => {
    try {
      const params = { company_id: cid, limit: 500 };
      if (filterByJob && jobId) params.job_id = jobId;
      const res = await RecruitmentAPI.getApplicants(params);
      // New /api/recruitment/pipeline returns a bare array. Old shapes
      // wrapped in .data / .rows / .applications are still handled for
      // backwards compatibility.
      const raw = Array.isArray(res) ? res
                : Array.isArray(res?.data)         ? res.data
                : Array.isArray(res?.rows)         ? res.rows
                : Array.isArray(res?.applications) ? res.applications
                : [];
      setApplicants(raw.map(normaliseApplicant).filter(Boolean));
    } catch (e) {
      console.warn('Pipeline fetch error:', e?.message);
      Alert.alert('Could not load applicants', e?.message || 'Please try again.');
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadCompanyData(setCompanyLogo, setCompanyName, null);
    await fetchApplicants(companyId);
    setRefreshing(false);
  };

  // ── Computed ─────────────────────────────────────────────────────────────
  const sharedParams = { companyId, companyLogo, companyName };

  const filtered = applicants.filter(a => {
    const matchSearch = !search || String(a.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
      String(a.email ?? '').toLowerCase().includes(search.toLowerCase());
    const matchStage = filterStage === 'all' || (a.stage || '').toLowerCase() === filterStage;
    return matchSearch && matchStage;
  });

  const byStage = (stageKey) => filtered.filter(a => (a.stage || 'new').toLowerCase() === stageKey.toLowerCase());

  const stats = {
    total:       applicants.length,
    new:         applicants.filter(a => a.stage === 'new').length,
    shortlisted: applicants.filter(a => a.stage === 'shortlisted').length,
    interviewed: applicants.filter(a => a.stage === 'interviewed').length,
    offered:     applicants.filter(a => a.stage === 'offered').length,
    hired:       applicants.filter(a => a.stage === 'hired').length,
    rejected:    applicants.filter(a => a.stage === 'rejected').length,
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const moveToStage = async (applicant, stage) => {
    setSaving(true);
    try {
      await RecruitmentAPI.updateStage(applicant.id, stage);
      setApplicants(prev => prev.map(a => a.id === applicant.id ? { ...a, stage } : a));
    } catch (e) { Alert.alert('Error', e.message); }
    finally { setSaving(false); }
  };

  const deleteApplicant = (applicant) => {
    Alert.alert('Remove Applicant', `Remove ${applicant.name}?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: async () => {
        try {
          await RecruitmentAPI.deleteApplicant(applicant.id);
          setApplicants(prev => prev.filter(a => a.id !== applicant.id));
        } catch (e) { Alert.alert('Error', e.message); }
      }},
    ]);
  };

  const handleAddApplicant = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      Alert.alert('Fill required fields', 'First name, last name, and email are required.');
      return;
    }
    if (!companyId) { Alert.alert('No company', 'Cannot add applicant without a company.'); return; }
    setSaving(true);
    try {
      // Real POST — same canonical recruitment apply route the candidate side
      // uses, so the row gets written to both applications + job_applications
      // and immediately appears in every applicant view including the
      // candidate's own dashboard (if they later sign in with this email).
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      const payload = {
        candidate_name:  fullName,
        name:            fullName,
        candidate_email: formData.email,
        email:           formData.email,
        candidate_phone: formData.phone || '',
        phone:           formData.phone || '',
        job_id:          jobId || formData.job_id || null,
        company_id:      companyId,
        job_title:       jobTitle || formData.position || '',
        current_title:   formData.position || '',
        stage:           'new',
        status:          'new',
        source:          'recruiter_manual',
        applied_date:    new Date().toISOString(),
      };
      const res = await apiFetch('/api/recruitment/apply', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (res?.error || res?.success === false) {
        throw new Error(res?.message || 'Server rejected the new applicant');
      }
      const newRow = res?.data || res?.application || res || payload;
      const newApp = normaliseApplicant({ ...payload, ...newRow, id: newRow.id || res?.id || Date.now() });
      setApplicants(prev => [newApp, ...prev]);
      setAddModal(false);
      setFormData({});
    } catch (e) {
      Alert.alert('Could not add applicant', e?.message || 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render Header ─────────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={['#0e4e96', '#1e7be2']} style={{ paddingBottom: 12 }}>
      <View style={S.headerRow}>
        <TouchableOpacity style={S.hBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={20} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1, marginHorizontal: 12 }}>
          <Text style={S.hTitle}>{filterByJob && jobTitle ? jobTitle : 'All Applicants'}</Text>
          <Text style={S.hSub}>{applicants.length} total · {companyName || 'Recruitment Pipeline'}</Text>
        </View>
        {companyLogo
          ? <Image source={{ uri: companyLogo }} style={S.hLogo} />
          : <View style={S.hLogoFb}><Text style={{ color: '#fff', fontWeight: '800' }}>{(companyName || 'C')[0]}</Text></View>}
        <TouchableOpacity style={[S.hBtn, { marginLeft: 8 }]} onPress={() => setViewMode(v => v === 'kanban' ? 'list' : 'kanban')}>
          <MaterialCommunityIcons name={viewMode === 'kanban' ? 'format-list-bulleted' : 'view-column'} size={20} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity style={[S.hBtn, { marginLeft: 8 }]} onPress={() => setAddModal(true)}>
          <MaterialCommunityIcons name="account-plus" size={20} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Stats */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 14, gap: 8 }}>
        {[
          { label: 'Total', val: stats.total, color: '#fff' },
          { label: 'New', val: stats.new, color: '#93C5FD' },
          { label: 'Shortlisted', val: stats.shortlisted, color: '#6EE7B7' },
          { label: 'Interviewed', val: stats.interviewed, color: '#C4B5FD' },
          { label: 'Offered', val: stats.offered, color: '#FCD34D' },
          { label: 'Hired', val: stats.hired, color: '#34D399' },
          { label: 'Rejected', val: stats.rejected, color: '#FCA5A5' },
        ].map(s => (
          <TouchableOpacity key={s.label} style={S.statChip}
            onPress={() => { setActiveTab('pipeline'); setFilterStage(s.label === 'Total' ? 'all' : String(s.label ?? '').toLowerCase()); }}>
            <Text style={[S.statVal, { color: s.color }]}>{s.val}</Text>
            <Text style={S.statLbl}>{s.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </LinearGradient>
  );

  // ── Render search + tabs ──────────────────────────────────────────────────
  const renderToolbar = () => (
    <View style={{ backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
      <View style={S.searchRow}>
        <Ionicons name="search" size={16} color="#9CA3AF" />
        <TextInput style={S.searchInput} value={search} onChangeText={setSearch}
          placeholder="Search applicants..." placeholderTextColor="#9CA3AF" />
        {search.length > 0 && <TouchableOpacity onPress={() => setSearch('')}><Ionicons name="close-circle" size={16} color="#9CA3AF" /></TouchableOpacity>}
      </View>
      <View style={{ flexDirection: 'row', borderBottomWidth: 0 }}>
        {[
          { id: 'pipeline', label: 'Pipeline', icon: 'pipe' },
          { id: 'list',     label: 'List',     icon: 'format-list-bulleted' },
          { id: 'analytics',label: 'Analytics',icon: 'chart-bar' },
        ].map(t => (
          <TouchableOpacity key={t.id} style={[S.tab, activeTab === t.id && S.tabActive]}
            onPress={() => setActiveTab(t.id)}>
            <MaterialCommunityIcons name={t.icon} size={15} color={activeTab === t.id ? PRIMARY : '#9CA3AF'} />
            <Text style={[S.tabText, activeTab === t.id && { color: PRIMARY, fontWeight: '700' }]}>{t.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  // ── Kanban column ─────────────────────────────────────────────────────────
  const renderKanbanColumn = (stage) => {
    const cards = byStage(stage.key);
    const cfg = STATUS_CFG[stage.label] || { color: stage.color, bg: stage.color + '15', icon: stage.icon };
    return (
      <View key={stage.key} style={[S.kanbanCol, { borderTopColor: stage.color }]}>
        <View style={S.kanbanColHeader}>
          <MaterialCommunityIcons name={stage.icon} size={15} color={stage.color} />
          <Text style={[S.kanbanColTitle, { color: stage.color }]}>{stage.label}</Text>
          <View style={[S.kanbanCount, { backgroundColor: stage.color }]}>
            <Text style={{ fontSize: 11, color: '#fff', fontWeight: '800' }}>{cards.length}</Text>
          </View>
        </View>
        {cards.map(a => renderKanbanCard(a, stage))}
        {cards.length === 0 && (
          <Text style={{ fontSize: 12, color: '#D1D5DB', textAlign: 'center', paddingVertical: 20, fontStyle: 'italic' }}>
            No applicants
          </Text>
        )}
      </View>
    );
  };

  const renderKanbanCard = (a, stage) => (
    <TouchableOpacity key={a.id} style={S.kanbanCard}
      onPress={() => navigation.navigate('ApplicantDetailsScreen', { ...sharedParams, applicant: a, applicantId: a.id })}
      onLongPress={() => setDetailApplicant(a)}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Avatar name={a.name} uri={a.avatar} size={30} />
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 13, fontWeight: '700', color: '#111827' }} numberOfLines={1}>{a.name}</Text>
          <Text style={{ fontSize: 11, color: '#6B7280' }} numberOfLines={1}>{a.currentRole || a.jobTitle || '—'}</Text>
        </View>
      </View>
      {a.aiScore != null && (
        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 6, gap: 4 }}>
          <MaterialCommunityIcons name="robot-outline" size={12} color="#8B5CF6" />
          <Text style={{ fontSize: 11, color: '#8B5CF6', fontWeight: '700' }}>{a.aiScore}%</Text>
        </View>
      )}
      {a.appliedAt && <Text style={{ fontSize: 10, color: '#9CA3AF', marginTop: 4 }}>{timeAgo(a.appliedAt)}</Text>}
    </TouchableOpacity>
  );

  // ── List card ─────────────────────────────────────────────────────────────
  const renderListCard = ({ item }) => {
    const stg = item.stage?.charAt(0).toUpperCase() + item.stage?.slice(1) || 'New';
    const cfg = STATUS_CFG[stg] || STATUS_CFG.New;
    return (
      <TouchableOpacity style={S.listCard}
        onPress={() => navigation.navigate('ApplicantDetailsScreen', { ...sharedParams, applicant: item, applicantId: item.id })}
        activeOpacity={0.85}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
          <Avatar name={item.name} uri={item.avatar} size={46} />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={{ fontSize: 15, fontWeight: '800', color: '#111827' }}>{item.name}</Text>
            <Text style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{item.currentRole || item.jobTitle || '—'}</Text>
            {item.currentCompany && <Text style={{ fontSize: 12, color: '#9CA3AF', marginTop: 1 }}>{item.currentCompany}</Text>}
          </View>
          <View style={{ alignItems: 'flex-end', gap: 4 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: cfg.bg, borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3 }}>
              <MaterialCommunityIcons name={cfg.icon} size={11} color={cfg.color} />
              <Text style={{ fontSize: 10, color: cfg.color, fontWeight: '700', marginLeft: 3 }}>{stg}</Text>
            </View>
            {item.aiScore != null && (
              <Text style={{ fontSize: 11, color: '#8B5CF6', fontWeight: '700' }}>🤖 {item.aiScore}%</Text>
            )}
          </View>
        </View>
        {/* Quick move actions */}
        <View style={{ flexDirection: 'row', gap: 8, marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#F8FAFC' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, flex: 1 }}>
            <Ionicons name="time-outline" size={12} color="#9CA3AF" />
            <Text style={{ fontSize: 11, color: '#9CA3AF' }}>{item.appliedAt ? timeAgo(item.appliedAt) : '—'}</Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {STAGES.filter(s => s.key !== (item.stage || 'new')).slice(0, 3).map(s => (
              <TouchableOpacity key={s.key}
                style={{ paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, backgroundColor: s.color + '15', borderWidth: 1, borderColor: s.color + '30' }}
                onPress={() => moveToStage(item, s.key)}>
                <Text style={{ fontSize: 10, color: s.color, fontWeight: '700' }}>{s.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <TouchableOpacity onPress={() => deleteApplicant(item)}>
            <MaterialCommunityIcons name="delete-outline" size={18} color="#EF4444" />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  // ── Analytics tab ─────────────────────────────────────────────────────────
  const renderAnalytics = () => {
    const total = applicants.length || 1;
    return (
      <ScrollView contentContainerStyle={{ padding: 16 }}>
        {STAGES.map(s => {
          const cnt = byStage(s.key).length;
          const pct = Math.round((cnt / total) * 100);
          return (
            <View key={s.key} style={{ backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 10, flexDirection: 'row', alignItems: 'center' }}>
              <MaterialCommunityIcons name={s.icon} size={20} color={s.color} style={{ marginRight: 12 }} />
              <Text style={{ fontSize: 14, fontWeight: '600', color: '#374151', width: 90 }}>{s.label}</Text>
              <View style={{ flex: 1, height: 10, backgroundColor: '#F3F4F6', borderRadius: 5, marginHorizontal: 10, overflow: 'hidden' }}>
                <View style={{ height: 10, borderRadius: 5, backgroundColor: s.color, width: `${pct}%` }} />
              </View>
              <Text style={{ fontSize: 13, fontWeight: '800', color: s.color, width: 28, textAlign: 'right' }}>{cnt}</Text>
              <Text style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 6, width: 36 }}>{pct}%</Text>
            </View>
          );
        })}
      </ScrollView>
    );
  };

  // ── Add Applicant Modal ───────────────────────────────────────────────────
  const renderAddModal = () => (
    <Modal visible={addModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setAddModal(false)}>
      <View style={{ flex: 1, backgroundColor: '#fff' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
          <Text style={{ fontSize: 18, fontWeight: '800', color: '#111827' }}>Add Applicant</Text>
          <TouchableOpacity style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#F3F4F6', alignItems: 'center', justifyContent: 'center' }}
            onPress={() => { setAddModal(false); setFormData({}); }}>
            <MaterialCommunityIcons name="close" size={20} color="#6B7280" />
          </TouchableOpacity>
        </View>
        <ScrollView contentContainerStyle={{ padding: 20 }}>
          {[
            { key: 'firstName', label: 'First Name *', placeholder: 'John' },
            { key: 'lastName',  label: 'Last Name *',  placeholder: 'Smith' },
            { key: 'email',     label: 'Email *',      placeholder: 'john@email.com', keyboardType: 'email-address' },
            { key: 'phone',     label: 'Phone',        placeholder: '+44 7700 900000', keyboardType: 'phone-pad' },
            { key: 'position',  label: 'Applied For',  placeholder: jobTitle || 'Job title' },
            { key: 'location',  label: 'Location',     placeholder: 'London, UK' },
          ].map(f => (
            <View key={f.key} style={{ marginBottom: 14 }}>
              <Text style={{ fontSize: 13, fontWeight: '700', color: '#374151', marginBottom: 6 }}>{f.label}</Text>
              <TextInput
                style={{ backgroundColor: '#F9FAFB', borderWidth: 1.5, borderColor: '#E5E7EB', borderRadius: 12, paddingHorizontal: 14, paddingVertical: 11, fontSize: 14, color: '#111827' }}
                value={formData[f.key] || ''}
                onChangeText={v => setFormData(prev => ({ ...prev, [f.key]: v }))}
                placeholder={f.placeholder}
                placeholderTextColor="#D1D5DB"
                keyboardType={f.keyboardType}
                autoCapitalize={f.keyboardType === 'email-address' ? 'none' : 'words'}
              />
            </View>
          ))}
          <TouchableOpacity
            style={{ backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 14, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 8, marginTop: 8 }}
            onPress={handleAddApplicant} disabled={saving}>
            {saving ? <ActivityIndicator size={18} color="#fff" /> : <>
              <MaterialCommunityIcons name="account-plus" size={18} color="#fff" />
              <Text style={{ color: '#fff', fontWeight: '800', fontSize: 16 }}>Add Applicant</Text>
            </>}
          </TouchableOpacity>
        </ScrollView>
      </View>
    </Modal>
  );

  // ── Quick Action Sheet ────────────────────────────────────────────────────
  const renderQuickActions = () => (
    <Modal visible={!!detailApplicant} transparent animationType="slide" onRequestClose={() => setDetailApplicant(null)}>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' }}>
        <View style={{ backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 22, paddingBottom: Platform.OS === 'ios' ? 36 : 24 }}>
          <Text style={{ fontSize: 17, fontWeight: '800', color: '#111827', marginBottom: 16 }}>{detailApplicant?.name}</Text>
          <TouchableOpacity style={S.qaRow} onPress={() => { setDetailApplicant(null); navigation.navigate('ApplicantDetailsScreen', { ...sharedParams, applicant: detailApplicant, applicantId: detailApplicant?.id }); }}>
            <MaterialCommunityIcons name="account-details" size={20} color="#0e4e96" />
            <Text style={S.qaLabel}>View Full Profile</Text>
            <Ionicons name="chevron-forward" size={18} color="#9CA3AF" />
          </TouchableOpacity>
          <Text style={{ fontSize: 13, fontWeight: '700', color: '#9CA3AF', marginTop: 14, marginBottom: 8 }}>MOVE TO STAGE</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {STAGES.map(s => (
              <TouchableOpacity key={s.key}
                style={{ paddingHorizontal: 14, paddingVertical: 9, borderRadius: 20, backgroundColor: s.color + '15', borderWidth: 1.5, borderColor: s.color + '40' }}
                onPress={() => { moveToStage(detailApplicant, s.key); setDetailApplicant(null); }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: s.color }}>{s.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <TouchableOpacity style={[S.qaRow, { marginTop: 16, borderTopWidth: 1, borderTopColor: '#F8FAFC', paddingTop: 14 }]}
            onPress={() => { setDetailApplicant(null); deleteApplicant(detailApplicant); }}>
            <MaterialCommunityIcons name="delete-outline" size={20} color="#EF4444" />
            <Text style={[S.qaLabel, { color: '#EF4444' }]}>Remove Applicant</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[S.qaRow]} onPress={() => setDetailApplicant(null)}>
            <MaterialCommunityIcons name="close" size={20} color="#6B7280" />
            <Text style={[S.qaLabel, { color: '#6B7280' }]}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#F3F4F6', justifyContent: 'center', alignItems: 'center' }}>
      <ActivityIndicator size={40} color={PRIMARY} />
      <Text style={{ marginTop: 14, color: '#9CA3AF', fontSize: 15 }}>Loading pipeline...</Text>
    </SafeAreaView>
  );

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#F3F4F6' }}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}
      {renderToolbar()}

      {activeTab === 'analytics' ? renderAnalytics() :
       viewMode === 'kanban' && activeTab === 'pipeline' ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]} />}
          contentContainerStyle={{ padding: 14, gap: 12 }}>
          {STAGES.map(s => renderKanbanColumn(s))}
        </ScrollView>
       ) : (
        <FlatList
          data={filtered}
          keyExtractor={i => i.id}
          renderItem={renderListCard}
          contentContainerStyle={{ padding: 14 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]} />}
          ListEmptyComponent={
            <View style={{ alignItems: 'center', paddingVertical: 60 }}>
              <MaterialCommunityIcons name="account-search-outline" size={56} color="#D1D5DB" />
              <Text style={{ fontSize: 16, color: '#9CA3AF', marginTop: 14 }}>No applicants found</Text>
            </View>
          }
        />
       )
      }

      {renderAddModal()}
      {renderQuickActions()}
    </SafeAreaView>
  );
}

const S = StyleSheet.create({
  headerRow:   { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingTop: Platform.OS === 'android' ? 28 : 8, paddingBottom: 8 },
  hBtn:        { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  hTitle:      { fontSize: 16, fontWeight: '900', color: '#fff' },
  hSub:        { fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 1 },
  hLogo:       { width: 32, height: 32, borderRadius: 16, borderWidth: 2, borderColor: 'rgba(255,255,255,0.5)' },
  hLogoFb:     { width: 32, height: 32, borderRadius: 16, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  statChip:    { backgroundColor: 'rgba(255,255,255,0.18)', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 7, alignItems: 'center', minWidth: 52, marginBottom: 4 },
  statVal:     { fontSize: 16, fontWeight: '900' },
  statLbl:     { fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 1 },
  searchRow:   { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: '#F3F4F6', gap: 8 },
  searchInput: { flex: 1, fontSize: 14, color: '#111827' },
  tab:         { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingVertical: 10 },
  tabActive:   { borderBottomWidth: 2, borderBottomColor: '#0e4e96' },
  tabText:     { fontSize: 12, color: '#9CA3AF', fontWeight: '600' },

  kanbanCol:        { width: 190, backgroundColor: '#fff', borderRadius: 14, borderTopWidth: 3, padding: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2, alignSelf: 'flex-start' },
  kanbanColHeader:  { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 10, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  kanbanColTitle:   { fontSize: 13, fontWeight: '800', flex: 1 },
  kanbanCount:      { width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  kanbanCard:       { backgroundColor: '#F9FAFB', borderRadius: 10, padding: 10, marginBottom: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.03, shadowRadius: 2, elevation: 1 },

  listCard:         { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },

  qaRow:       { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12 },
  qaLabel:     { flex: 1, fontSize: 15, color: '#111827', fontWeight: '600' },
});
